import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { Modal } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import {
  FaSearch,
  FaPlus,
  FaTimes,
  FaUpload,
  FaCheckCircle,
} from "react-icons/fa";
import Button from "../../../components/general/button/Button";
import Pagination from "../../../components/general/pagination/Pagination";
import ActionDropdown from "../../../components/seller/dropdowns/action/ActionDropdowns";
import SellerLayout from "../../../layouts/SellerLayout";
import MyContext from "../../../context/MyContext";
import { SellerProductsPageSkeleton } from "../../../components/general/skeleton/SkeletonBlock";
import "../../../assets/css/seller/products/SellerProducts.css";

const WEIGHT_BASED_UNITS = ["kg", "g"];

const ProductVerificationModal = ({ show, onHide }) => {
  const navigate = useNavigate();
  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      backdrop="static"
      keyboard={false}
      className="verification-modal"
    >
      <Modal.Body className="verification-modal-body">
        <button className="verification-close-btn" onClick={onHide}>
          <FaTimes />
        </button>

        <div className="verification-icon">
          <FaCheckCircle />
        </div>

        <h2 className="verification-title">Verification Required</h2>

        <p className="verification-text">
          You need to complete your KYC verification before you can add
          products. This helps build trust with customers.
        </p>

        <div className="verification-actions">
          <Button
            variant="gradient"
            onClick={() => {
              onHide();
              navigate("/seller/settings", {
                state: { activeTab: "verification" },
              });
            }}
            className="verify-now-btn"
          >
            Complete Verification
          </Button>
          <Button onClick={onHide} className="skip-btn">
            Cancel
          </Button>
        </div>
      </Modal.Body>
    </Modal>
  );
};

const SellerProducts = () => {
  const navigate = useNavigate();
  const { baseUrl, baseUrlForFiles, authToken } = useContext(MyContext);

  const [productsList, setProductsList] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const [showAddModal, setShowAddModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);

  const [openActionMenu, setOpenActionMenu] = useState(null);
  const [categoriesList, setCategoriesList] = useState([]);
  const [subcategoriesList, setSubcategoriesList] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");

  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [price, setPrice] = useState("");
  const [stock, setStock] = useState("");
  const [minOrderQuantity, setMinOrderQuantity] = useState("1");
  const [unit, setUnit] = useState("kg");
  const [weightKg, setWeightKg] = useState("");
  const [bulkThresholdQuantity, setBulkThresholdQuantity] = useState("");
  const [bulkDiscountPercent, setBulkDiscountPercent] = useState("");
  const [isReturnable, setIsReturnable] = useState(true);
  const [description, setDescription] = useState("");
  const [image, setImage] = useState(null);
  // ── NEW: existing image ka path — edit modal mein current picture
  //     dikhane ke liye, jab tak user nayi image select na kare ──
  const [existingImage, setExistingImage] = useState(null);
  // ── FIXED: category ab controlled state se track hota hai, taake
  //     edit ke waqt product ki maujooda category dropdown mein
  //     pehle se selected dikhe ──
  const [kmcategory_id, setKmcategoryId] = useState("");
  const [kmsubcategory_id, setKmsubcategoryId] = useState("");
  const [idToUpdateDeleteProduct, setIdToUpdateDeleteProduct] = useState("");

  // ── Verification States — ab isVerified backend se hi profile fetch ke sath aayega ──
  const [isSellerVerified, setIsSellerVerified] = useState(false);
  const [showVerificationModal, setShowVerificationModal] = useState(false);

  // ─── Check Seller Verification Status — GET + Bearer, no kmseller_id ──
  const checkVerificationStatus = () => {
    axios
      .post(
        `${baseUrl}/seller/profile/viewProfile`,
        {},
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        },
      )
      .then((response) => {
        const kyc = response.data.seller?.kmkyc;
        setIsSellerVerified(kyc?.isVerified === "verified");
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const generateSlug = (value) => {
    return value
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, "")
      .replace(/[\s_-]+/g, "-")
      .replace(/^-+|-+$/g, "");
  };

  const resetFields = () => {
    setName("");
    setSlug("");
    setPrice("");
    setStock("");
    setMinOrderQuantity("1");
    setUnit("kg");
    setWeightKg("");
    setBulkThresholdQuantity("");
    setIsReturnable(true);
    setBulkDiscountPercent("");
    setDescription("");
    setImage(null);
    setExistingImage(null);
    setKmcategoryId("");
    setKmsubcategoryId("");
    setIdToUpdateDeleteProduct("");
    setSubcategoriesList([]);
  };

  // ─── View All Products — GET + Bearer, no kmseller_id ──
  const viewAllProducts = () => {
    axios
      .get(`${baseUrl}/seller/product/viewProduct`, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((response) => {
        if (response.data.hasOwnProperty("error")) {
          setErrorMessage(response.data.error);
          Swal.fire({
            icon: "error",
            title: "Error",
            text: response.data.error,
          });
        } else {
          setProductsList(response.data.product);
        }
      })
      .catch((error) => {
        console.log(error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Products cannot load .",
        });
      })
      .finally(() => setLoading(false));
  };

  // ─── View All Categories — public, unchanged ──
  const viewAllCategories = () => {
    axios
      .get(`${baseUrl}/seller/category/viewCategories`)
      .then((response) => {
        if (response.data.success == true) {
          setCategoriesList(response.data.categories);
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  // ─── View Subcategories — public, unchanged ──
  const viewSubcategories = (categoryId) => {
    const formData = new FormData();
    formData.append("kmcategory_id", categoryId);
    axios
      .post(`${baseUrl}/seller/subcategory/viewSubcategories`, formData)
      .then((response) => {
        if (response.data.success == true) {
          setSubcategoriesList(response.data.subcategories);
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  // ── FIXED: weight ab required hai (jab unit weight-based na ho) —
  //     add/update dono submit hone se pehle yahan check hota hai ──
  const validateProductForm = () => {
    if (!WEIGHT_BASED_UNITS.includes(unit) && !weightKg) {
      Swal.fire({
        icon: "warning",
        title: "Weight Required",
        text: "Please enter the weight per unit (kg) for this product.",
      });
      return false;
    }
    return true;
  };

  // ─── Add Product — POST + Bearer, no kmseller_id ──
  const addProduct = () => {
    if (!validateProductForm()) return;

    const formData = new FormData();
    formData.append("name", name);
    formData.append("slug", slug);
    formData.append("price", price);
    formData.append("stock", stock);
    formData.append("min_order_quantity", minOrderQuantity);
    formData.append("unit", unit);
    formData.append("description", description);
    formData.append("Kmsubcategory_id", kmsubcategory_id);
    formData.append("image", image);

    if (!WEIGHT_BASED_UNITS.includes(unit)) {
      formData.append("weight_kg", weightKg);
    }
    if (bulkThresholdQuantity) {
      formData.append("bulk_threshold_quantity", bulkThresholdQuantity);
      formData.append("bulk_discount_percent", bulkDiscountPercent || 0);
    }
    formData.append("is_returnable", isReturnable ? 1 : 0);

    axios
      .post(`${baseUrl}/seller/product/addProduct`, formData, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((response) => {
        if (response.data.success == true) {
          Swal.fire({
            icon: "success",
            title: "Success",
            text: "Product added successfully!",
          });
          setShowAddModal(false);
          viewAllProducts();
          resetFields();
        } else {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: "Failed to add product.",
          });
        }
      })
      .catch((error) => {
        console.log(error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to add product.",
        });
      });
  };

  // ─── Update Product — Bearer added ──
  const updateProduct = () => {
    if (!validateProductForm()) return;

    const formData = new FormData();
    formData.append("id", idToUpdateDeleteProduct);
    formData.append("name", name);
    formData.append("slug", slug);
    formData.append("price", price);
    formData.append("stock", stock);
    formData.append("min_order_quantity", minOrderQuantity);
    formData.append("unit", unit);
    formData.append("description", description);
    formData.append("Kmsubcategory_id", kmsubcategory_id);
    if (image) {
      formData.append("image", image);
    }

    if (!WEIGHT_BASED_UNITS.includes(unit)) {
      formData.append("weight_kg", weightKg);
    }
    if (bulkThresholdQuantity) {
      formData.append("bulk_threshold_quantity", bulkThresholdQuantity);
      formData.append("bulk_discount_percent", bulkDiscountPercent || 0);
    }
    formData.append("is_returnable", isReturnable ? 1 : 0);

    axios
      .post(`${baseUrl}/seller/product/updateProduct`, formData, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((response) => {
        if (response.data.success == true) {
          Swal.fire({
            icon: "success",
            title: "Success",
            text: "Product updated successfully!",
          });
          setShowAddModal(false);
          viewAllProducts();
          resetFields();
        } else {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: "Failed to update product.",
          });
        }
      })
      .catch((error) => {
        console.log(error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to update product.",
        });
      });
  };

  // ─── Delete Product — Bearer added ──
  const deleteProduct = (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "This product will be permanently deleted!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    }).then((result) => {
      if (result.isConfirmed) {
        const formData = new FormData();
        formData.append("id", id);
        axios
          .post(`${baseUrl}/seller/product/deleteProduct`, formData, {
            headers: { Authorization: `Bearer ${authToken}` },
          })
          .then((response) => {
            if (response.data.success == true) {
              Swal.fire({
                icon: "success",
                title: "Deleted!",
                text: "Product deleted successfully!",
              });
              viewAllProducts();
            } else {
              Swal.fire({
                icon: "error",
                title: "Error",
                text: "Failed to delete product.",
              });
            }
          })
          .catch((error) => {
            console.log(error);
            Swal.fire({
              icon: "error",
              title: "Error",
              text: "Failed to delete product.",
            });
          });
      }
    });
  };

  // ─── Restock Product — Bearer added ──
  const restockProduct = (product) => {
    Swal.fire({
      title: `Restock "${product.name}"`,
      html: `<p style="color:#6b7280;font-size:0.85rem;margin-bottom:8px;">Current Stock: <strong>${product.stock}</strong></p>`,
      input: "number",
      inputLabel: "Quantity to add",
      inputPlaceholder: "e.g. 20",
      inputAttributes: { min: 1 },
      showCancelButton: true,
      confirmButtonText: "Restock",
      confirmButtonColor: "#097d43",
      cancelButtonColor: "#6c757d",
      inputValidator: (value) => {
        if (!value || Number(value) <= 0) {
          return "Please enter a valid quantity";
        }
      },
    }).then((result) => {
      if (result.isConfirmed) {
        const formData = new FormData();
        formData.append("id", product.id);
        formData.append("quantity", result.value);

        axios
          .post(`${baseUrl}/seller/product/restockProduct`, formData, {
            headers: { Authorization: `Bearer ${authToken}` },
          })
          .then((response) => {
            if (response.data.success == true) {
              Swal.fire({
                icon: "success",
                title: "Restocked!",
                text: `Stock updated to ${response.data.stock}`,
              });
              viewAllProducts();
            } else {
              Swal.fire({
                icon: "error",
                title: "Error",
                text: "Failed to restock product.",
              });
            }
          })
          .catch((error) => {
            console.log(error);
            Swal.fire({
              icon: "error",
              title: "Error",
              text: "Failed to restock product.",
            });
          });
      }
    });
  };

  useEffect(() => {
    if (!authToken) {
      setLoading(false);
      navigate("/seller/login");
      return;
    }
    viewAllProducts();
    checkVerificationStatus();
  }, [authToken]);

  useEffect(() => {
    viewAllCategories();
  }, []);

  const filteredProducts = productsList.filter((p) =>
    p.name?.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const getActions = (product) => [
    {
      label: "View Details",
      onClick: () => {
        setSelectedProduct(product);
        setShowViewModal(true);
        setOpenActionMenu(null);
      },
    },
    {
      label: "Edit",
      onClick: () => {
        setIsEditing(true);
        setIdToUpdateDeleteProduct(product.id);
        setName(product.name);
        setSlug(product.slug);
        setPrice(product.price);
        setStock(product.stock);
        setMinOrderQuantity(product.min_order_quantity || 1);
        setUnit(product.unit);
        setWeightKg(product.weight_kg || "");
        setBulkThresholdQuantity(product.bulk_threshold_quantity || "");
        setIsReturnable(product.is_returnable === undefined ? true : !!Number(product.is_returnable));
        setBulkDiscountPercent(product.bulk_discount_percent || "");
        setDescription(product.description);
        setImage(null);
        // ── NEW: current product image edit modal mein preview ke
        //     liye — jab tak seller nayi image select na kare, yehi
        //     dikhti rahegi ──
        setExistingImage(product.image || null);

        // ── FIXED: product ki maujooda category + subcategory ab
        //     dropdown mein pehle se selected show hongi. Category
        //     id nested relation (kmsubcategory.kmcategory.id) se
        //     milti hai. Subcategory id backend se kabhi
        //     "Kmsubcategory_id" (jo hum submit karte waqt bhejte
        //     hain) to kabhi "kmsubcategory_id"/relation ki apni id
        //     ke roop mein aati hai — isi wajah se select khali reh
        //     jata tha, ab teeno fallback try hote hain ──
        const currentCategoryId = product.kmsubcategory?.kmcategory?.id || "";
        setKmcategoryId(currentCategoryId);
        setKmsubcategoryId(
          product.Kmsubcategory_id ??
            product.kmsubcategory_id ??
            product.kmsubcategory?.id ??
            "",
        );
        if (currentCategoryId) {
          viewSubcategories(currentCategoryId);
        } else {
          setSubcategoriesList([]);
        }

        setShowAddModal(true);
        setOpenActionMenu(null);
      },
    },
    {
      label: "Restock",
      onClick: () => {
        restockProduct(product);
        setOpenActionMenu(null);
      },
    },
    {
      label: "Delete",
      onClick: () => {
        deleteProduct(product.id);
        setOpenActionMenu(null);
      },
      variant: "danger",
    },
  ];

  if (loading) {
    return (
      <SellerLayout>
        <SellerProductsPageSkeleton />
      </SellerLayout>
    );
  }

  return (
    <SellerLayout>
      <div className="product-inventory-container">
        <div className="product-inventory-header">
          <div>
            <h1>Product Inventory</h1>
            <p>Manage your product catalog</p>
          </div>
          <Button
            variant="gradient"
            icon={<FaPlus />}
            iconPosition="left"
            onClick={() => {
              if (!isSellerVerified) {
                setShowVerificationModal(true);
              } else {
                setIsEditing(false);
                resetFields();
                setShowAddModal(true);
              }
            }}
          >
            Add Product
          </Button>
        </div>

        <ProductVerificationModal
          show={showVerificationModal}
          onHide={() => setShowVerificationModal(false)}
        />

        <div className="product-table-section">
          <div className="product-table-header">
            <div className="product-search">
              <input
                type="text"
                placeholder="Search products..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          <div className="product-table-wrapper">
            <table className="product-table">
              <thead>
                <tr>
                  <th>IMAGE</th>
                  <th>PRODUCT NAME</th>
                  <th>CATEGORY</th>
                  <th>SUB CATEGORY</th>
                  <th>UNIT</th>
                  <th>WEIGHT (KG)</th>
                  <th>PRICE</th>
                  <th>STOCK</th>
                  <th>MIN QTY</th>
                  <th>BULK</th>
                  <th>ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {filteredProducts
                  .slice(
                    (currentPage - 1) * itemsPerPage,
                    currentPage * itemsPerPage,
                  )
                  .map((product) => (
                    <tr key={product.id}>
                      <td>
                        <img
                          src={`${baseUrlForFiles}/${product.image}`}
                          alt={product.name}
                          className="product-image"
                        />
                      </td>
                      <td className="product-name">{product.name}</td>
                      <td>{product.kmsubcategory?.kmcategory?.categoryname}</td>
                      <td>{product.kmsubcategory?.subcategoryname}</td>
                      <td>
                        <span className="product-unit-badge">
                          {product.unit}
                        </span>
                      </td>
                      <td>
                        {WEIGHT_BASED_UNITS.includes(product.unit)
                          ? "1 per unit"
                          : product.weight_kg
                            ? `${product.weight_kg} kg`
                            : "—"}
                      </td>
                      <td className="product-price">
                        PKR {product.price?.toLocaleString()}
                      </td>
                      <td>
                        <span
                          className={`product-status ${
                            product.stock > 0 ? "in-stock" : "out-stock"
                          }`}
                        >
                          {product.stock > 0 ? product.stock : "Out of Stock"}
                        </span>
                      </td>
                      <td>{product.min_order_quantity}</td>
                      <td>
                        {product.bulk_threshold_quantity ? (
                          <span
                            className="product-unit-badge"
                            title={`${product.bulk_discount_percent || 0}% off at ${product.bulk_threshold_quantity}+`}
                          >
                            {product.bulk_threshold_quantity}+ (
                            {product.bulk_discount_percent || 0}%)
                          </span>
                        ) : (
                          <span style={{ color: "#999", fontSize: 12 }}>
                            Same-city only
                          </span>
                        )}
                      </td>
                      <td>
                        <ActionDropdown
                          isOpen={openActionMenu === product.id}
                          onToggle={() =>
                            setOpenActionMenu(
                              openActionMenu === product.id ? null : product.id,
                            )
                          }
                          actions={getActions(product)}
                        />
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>

          <Pagination
            totalItems={filteredProducts.length}
            itemsPerPage={itemsPerPage}
            currentPage={currentPage}
            onPageChange={setCurrentPage}
            onItemsPerPageChange={(n) => {
              setItemsPerPage(n);
              setCurrentPage(1);
            }}
          />
        </div>

        {showAddModal && (
          <div
            className="ksa-modal-overlay"
            onClick={() => {
              setShowAddModal(false);
              resetFields();
            }}
          >
            <div
              className="ksa-modal ksa-modal-md ksa-category-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="ksa-category-modal-header">
                <h3 className="ksa-category-modal-title">
                  {isEditing ? "Edit Product" : "Add New Product"}
                </h3>
                <button
                  className="ksa-modal-close"
                  onClick={() => {
                    setShowAddModal(false);
                    resetFields();
                  }}
                >
                  <FaTimes size={18} />
                </button>
              </div>

              <div className="ksa-category-modal-body">
                <div className="ksa-category-form-group">
                  <label>Product Name *</label>
                  <input
                    type="text"
                    className="ksa-category-input"
                    placeholder="e.g. Wheat Seeds"
                    value={name}
                    onChange={(e) => {
                      setName(e.target.value);
                      setSlug(generateSlug(e.target.value));
                    }}
                  />
                </div>

                <div className="ksa-category-form-group">
                  <label>Slug *</label>
                  <input
                    type="text"
                    className="ksa-category-input"
                    placeholder="auto-generated"
                    value={slug}
                    onChange={(e) => setSlug(e.target.value)}
                  />
                  <small style={{ color: "#999", fontSize: "0.8rem" }}>
                    Auto-generated from name
                  </small>
                </div>

                <div className="ksa-category-form-group">
                  <label>Price (PKR) *</label>
                  <input
                    type="number"
                    className="ksa-category-input"
                    placeholder="e.g. 2800"
                    value={price}
                    onChange={(e) => setPrice(e.target.value)}
                  />
                </div>

                <div className="ksa-category-form-group">
                  <label>Stock *</label>
                  <input
                    type="number"
                    className="ksa-category-input"
                    placeholder="e.g. 50"
                    value={stock}
                    onChange={(e) => setStock(e.target.value)}
                  />
                </div>

                <div className="ksa-category-form-group">
                  <label>Minimum Order Quantity *</label>
                  <input
                    type="number"
                    className="ksa-category-input"
                    placeholder="e.g. 5"
                    min={1}
                    value={minOrderQuantity}
                    onChange={(e) => setMinOrderQuantity(e.target.value)}
                  />
                  <small style={{ color: "#999", fontSize: "0.8rem" }}>
                    Customer isse kam quantity order nahi kar sakega
                  </small>
                </div>

                <div className="ksa-category-form-group">
                  <label>Unit *</label>
                  <select
                    className="ksa-category-input"
                    value={unit}
                    onChange={(e) => setUnit(e.target.value)}
                  >
                    <option value="kg">kg</option>
                    <option value="g">g</option>
                    <option value="bag">bag</option>
                    <option value="pack">pack</option>
                    <option value="bottle">bottle</option>
                    <option value="liter">liter</option>
                    <option value="bundle">bundle</option>
                    <option value="piece">piece</option>
                  </select>
                </div>

                {!WEIGHT_BASED_UNITS.includes(unit) && (
                  <div className="ksa-category-form-group">
                    <label>Weight per Unit (kg) *</label>
                    <input
                      type="number"
                      step="0.01"
                      min={0}
                      required
                      className="ksa-category-input"
                      placeholder="e.g. 0.06 (ek piece ka weight kg mein)"
                      value={weightKg}
                      onChange={(e) => setWeightKg(e.target.value)}
                    />
                    <small style={{ color: "#999", fontSize: "0.8rem" }}>
                      Ek {unit} ka weight kitne kg ka hai — delivery driver
                      assignment isi se hota hai
                    </small>
                  </div>
                )}

                {/* ── Bulk / Wholesale (Intercity Delivery) ── */}
                <div className="ksa-category-form-group">
                  <label>Bulk Threshold Quantity (optional)</label>
                  <input
                    type="number"
                    min={0}
                    className="ksa-category-input"
                    placeholder="e.g. 20"
                    value={bulkThresholdQuantity}
                    onChange={(e) => setBulkThresholdQuantity(e.target.value)}
                  />
                  <small style={{ color: "#999", fontSize: "0.8rem" }}>
                    Is se zyada ya barabar quantity order karne par ye product
                    intercity delivery ke liye eligible ho jata hai. Khali chhod
                    dein to ye product sirf same-city order ho sakega.
                  </small>
                </div>

                {bulkThresholdQuantity && (
                  <div className="ksa-category-form-group">
                    <label>Bulk Discount (%)</label>
                    <input
                      type="number"
                      step="0.01"
                      min={0}
                      max={100}
                      className="ksa-category-input"
                      placeholder="e.g. 10"
                      value={bulkDiscountPercent}
                      onChange={(e) => setBulkDiscountPercent(e.target.value)}
                    />
                    <small style={{ color: "#999", fontSize: "0.8rem" }}>
                      Threshold quantity pura hone par customer ko ye discount
                      automatically milega (same-city ya intercity, dono mein)
                    </small>
                  </div>
                )}

                {/* ── NEW: Returnable toggle — agar order mein ye
                     product shamil ho aur ye non-returnable ho, poora
                     order hi non-returnable ban jata hai (customer
                     "Reject at Delivery" use nahi kar sakega) ── */}
                <div className="ksa-category-form-group">
                  <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" }}>
                    <input
                      type="checkbox"
                      checked={isReturnable}
                      onChange={(e) => setIsReturnable(e.target.checked)}
                    />
                    This product is returnable
                  </label>
                  <small style={{ color: "#999", fontSize: "0.8rem" }}>
                    Agar unchecked karein, is product wale kisi bhi order ko
                    customer delivery ke waqt reject/return nahi kar sakega.
                  </small>
                </div>

                <div className="ksa-category-form-group">
                  <label>Category *</label>
                  <select
                    className="ksa-category-input"
                    value={kmcategory_id}
                    onChange={(e) => {
                      setKmcategoryId(e.target.value);
                      setKmsubcategoryId("");
                      if (e.target.value) {
                        viewSubcategories(e.target.value);
                      } else {
                        setSubcategoriesList([]);
                      }
                    }}
                  >
                    <option value="">Select Category</option>
                    {categoriesList.map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.categoryname}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="ksa-category-form-group">
                  <label>Sub Category *</label>
                  <select
                    className="ksa-category-input"
                    value={kmsubcategory_id}
                    onChange={(e) => setKmsubcategoryId(e.target.value)}
                    disabled={subcategoriesList.length === 0}
                  >
                    <option value="">Select Sub Category</option>
                    {subcategoriesList.map((sub) => (
                      <option key={sub.id} value={sub.id}>
                        {sub.subcategoryname}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="ksa-category-form-group">
                  <label>Description</label>
                  <textarea
                    className="ksa-category-input"
                    placeholder="Product description..."
                    rows={3}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                  />
                </div>

                <div className="ksa-category-form-group">
                  <label>Product Image *</label>
                  <div className="ksa-category-upload-area">
                    <input
                      type="file"
                      id="productImage"
                      style={{ display: "none" }}
                      onChange={(e) => setImage(e.target.files[0])}
                    />
                    {image ? (
                      <div style={{ display: "flex", alignItems: "center", gap: "10px", width: "100%" }}>
                        <label htmlFor="productImage" style={{ cursor: "pointer", flex: 1 }}>
                          <FaUpload size={28} />
                          <p>{image.name}</p>
                          <span>PNG, JPG (max 2MB)</span>
                        </label>
                        <button
                          type="button"
                          onClick={() => {
                            // ── NEW: nayi select ki hui image ko
                            //     remove karna — confirm Swal ab
                            //     didOpen ke zariye modal ke UPAR
                            //     dikhega (pehle z-index ki wajah se
                            //     modal ke neeche chup jata tha) ──
                            Swal.fire({
                              title: "Remove selected image?",
                              icon: "warning",
                              showCancelButton: true,
                              confirmButtonText: "Yes, remove",
                              cancelButtonText: "Cancel",
                              confirmButtonColor: "#d33",
                              cancelButtonColor: "#6c757d",
                              didOpen: () => {
                                const container = document.querySelector(".swal2-container");
                                if (container) container.style.zIndex = 999999;
                              },
                            }).then((result) => {
                              if (result.isConfirmed) {
                                setImage(null);
                              }
                            });
                          }}
                          style={{
                            border: "none",
                            background: "transparent",
                            color: "#d33",
                            cursor: "pointer",
                            fontSize: "1.1rem",
                          }}
                        >
                          <FaTimes />
                        </button>
                      </div>
                    ) : isEditing && existingImage ? (
                      <label
                        htmlFor="productImage"
                        style={{ cursor: "pointer", width: "100%", display: "flex", alignItems: "center", gap: "10px" }}
                      >
                        <img
                          src={`${baseUrlForFiles}/${existingImage}`}
                          alt="Current product"
                          style={{ width: 48, height: 48, objectFit: "cover", borderRadius: 8 }}
                        />
                        <span>Click to change image</span>
                      </label>
                    ) : (
                      <label
                        htmlFor="productImage"
                        style={{ cursor: "pointer", width: "100%" }}
                      >
                        <FaUpload size={28} />
                        <p>Click to upload image</p>
                        <span>PNG, JPG (max 2MB)</span>
                      </label>
                    )}
                  </div>
                </div>
              </div>

              <div className="ksa-category-modal-footer">
                <button
                  className="outline-gradient"
                  onClick={() => {
                    setShowAddModal(false);
                    resetFields();
                  }}
                >
                  Cancel
                </button>
                <Button
                  variant="gradient"
                  onClick={isEditing ? updateProduct : addProduct}
                >
                  {isEditing ? "Update Product" : "Create Product"}
                </Button>
              </div>
            </div>
          </div>
        )}

        {showViewModal && selectedProduct && (
          <div
            className="ksa-modal-overlay"
            onClick={() => setShowViewModal(false)}
          >
            <div
              className="ksa-modal ksa-modal-lg ksa-category-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="ksa-category-modal-header">
                <h3 className="ksa-category-modal-title">Product Details</h3>
                <button
                  className="ksa-modal-close"
                  onClick={() => setShowViewModal(false)}
                >
                  <FaTimes size={18} />
                </button>
              </div>

              <div className="ksa-category-modal-body">
                <div style={{ textAlign: "center", marginBottom: "1rem" }}>
                  <img
                    src={`${baseUrlForFiles}/${selectedProduct.image}`}
                    alt={selectedProduct.name}
                    style={{
                      width: 120,
                      height: 120,
                      objectFit: "cover",
                      borderRadius: 12,
                    }}
                  />
                </div>

                <div className="ksa-category-form-group">
                  <label>Product Name</label>
                  <input
                    type="text"
                    className="ksa-category-input"
                    value={selectedProduct.name}
                    disabled
                  />
                </div>

                <div className="ksa-category-form-group">
                  <label>Slug</label>
                  <input
                    type="text"
                    className="ksa-category-input"
                    value={selectedProduct.slug}
                    disabled
                  />
                </div>

                <div className="ksa-category-form-group">
                  <label>Price (PKR)</label>
                  <input
                    type="text"
                    className="ksa-category-input"
                    value={selectedProduct.price}
                    disabled
                  />
                </div>

                <div className="ksa-category-form-group">
                  <label>Stock</label>
                  <input
                    type="text"
                    className="ksa-category-input"
                    value={selectedProduct.stock}
                    disabled
                  />
                </div>

                <div className="ksa-category-form-group">
                  <label>Minimum Order Quantity</label>
                  <input
                    type="text"
                    className="ksa-category-input"
                    value={selectedProduct.min_order_quantity}
                    disabled
                  />
                </div>

                <div className="ksa-category-form-group">
                  <label>Bulk Threshold Quantity</label>
                  <input
                    type="text"
                    className="ksa-category-input"
                    value={
                      selectedProduct.bulk_threshold_quantity ||
                      "Not set (same-city only)"
                    }
                    disabled
                  />
                </div>

                {selectedProduct.bulk_threshold_quantity && (
                  <div className="ksa-category-form-group">
                    <label>Bulk Discount (%)</label>
                    <input
                      type="text"
                      className="ksa-category-input"
                      value={selectedProduct.bulk_discount_percent || 0}
                      disabled
                    />
                  </div>
                )}

                <div className="ksa-category-form-group">
                  <label>Unit</label>
                  <input
                    type="text"
                    className="ksa-category-input"
                    value={selectedProduct.unit}
                    disabled
                  />
                </div>

                <div className="ksa-category-form-group">
                  <label>Weight per Unit (kg)</label>
                  <input
                    type="text"
                    className="ksa-category-input"
                    value={
                      WEIGHT_BASED_UNITS.includes(selectedProduct.unit)
                        ? "1 per unit"
                        : selectedProduct.weight_kg || "Not set"
                    }
                    disabled
                  />
                </div>

                <div className="ksa-category-form-group">
                  <label>Category</label>
                  <input
                    type="text"
                    className="ksa-category-input"
                    value={
                      selectedProduct.kmsubcategory?.kmcategory?.categoryname ||
                      ""
                    }
                    disabled
                  />
                </div>

                <div className="ksa-category-form-group">
                  <label>Sub Category</label>
                  <input
                    type="text"
                    className="ksa-category-input"
                    value={selectedProduct.kmsubcategory?.subcategoryname || ""}
                    disabled
                  />
                </div>

                <div className="ksa-category-form-group">
                  <label>Description</label>
                  <textarea
                    className="ksa-category-input"
                    value={selectedProduct.description || ""}
                    disabled
                    rows={3}
                  />
                </div>
              </div>

              <div className="ksa-category-modal-footer">
                <button
                  className="outline-gradient"
                  onClick={() => setShowViewModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </SellerLayout>
  );
};

export default SellerProducts;
